import React, { useState, useMemo, useEffect } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  TextField,
  FormHelperText
} from '@material-ui/core';
import axios from 'axios';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import yaml from 'js-yaml';
import debounce from 'lodash.debounce';

export const CreateAdoProject = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { onChange, formData, required, rawErrors, schema } = props;
  const [internalValue, setInternalValue] = useState(formData || '');
  const [validationError, setValidationError] = useState<string | null>(null);

  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [baseUrl, setBaseUrl] = useState('');

  useEffect(() => {
    discoveryApi.getBaseUrl('scaffolder')
      .then(url => setBaseUrl(url))
      .catch(err => console.error('Failed to get scaffolder baseUrl', err));
  }, [discoveryApi]);

  const checkProjectExists = async (value: string): Promise<boolean> => {
    const projectName = value.trim().toLowerCase();
    if (!projectName) {
      return false;
    }

    try {
      const { token } = await identityApi.getCredentials();
      const res = await axios.post(
        `${baseUrl}/aws/params`,
        { name: '/backstage/projects' },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      const rawValue: string = res.data.value || '';
      let projectMap: Record<string, unknown> = {};

      try {
        const parsed = yaml.load(rawValue);
        if (parsed && typeof parsed === 'object') {
          projectMap = parsed as Record<string, unknown>;
        }
      } catch {
        console.warn(
          'Could not parse /backstage/projects as YAML, falling back to CSV',
        );
      }

      // If YAML parse yields keys, use them; otherwise comma‐split
      const projectKeys =
        Object.keys(projectMap).length > 0
          ? Object.keys(projectMap)
          : rawValue.split(',').map(s => s.trim()).filter(s => s);

      const normalized = projectKeys.map(k => k.toLowerCase());
      return normalized.includes(projectName);
    } catch (e) {
      console.error('Error checking project existence:', e);
      return false;
    }
  };

  const debouncedValidate = useMemo(
    () =>
      debounce(async (value: string) => {
        const trimmed = value.trim();
        if (!trimmed) {
          setValidationError('Project field is required.');
          onChange('');
          return;
        }
        const exists = await checkProjectExists(trimmed);
        if (exists) {
          setValidationError('Project already exists in AWS Parameter Store.');
          onChange('');
        } else {
          setValidationError(null);
          onChange(trimmed);
        }
      }, 800),
    [onChange, baseUrl],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setInternalValue(v);
    onChange('');
    
    // Check if value contains only lowercase letters
    if (v && !/^[a-z]*$/.test(v)) {
      setValidationError('Only lowercase letters (a-z) are allowed. No spaces, capitals, or special characters.');
      return;
    }
    
    setValidationError(null);
    debouncedValidate(v);
  };

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    debouncedValidate.cancel();
    const v = e.target.value.trim();
    if (!v) {
      setValidationError('Project field is required.');
      onChange('');
      return;
    }
    
    // Check if value contains only lowercase letters
    if (!/^[a-z]+$/.test(v)) {
      setValidationError('Only lowercase letters (a-z) are allowed. No spaces, capitals, or special characters.');
      onChange('');
      return;
    }
    
    const exists = await checkProjectExists(v);
    if (exists) {
      setValidationError('Project already exists in AWS Parameter Store.');
      onChange('');
    } else {
      setValidationError(null);
      onChange(v);
    }
  };

  return (
    <FormControl
      fullWidth
      required={required}
      error={!!rawErrors?.length || !!validationError}
    >
      <TextField
        fullWidth
        required={required}
        label={schema.title || 'Project Name'}
        helperText={validationError || schema.description}
        value={internalValue}
        onChange={handleChange}
        onBlur={handleBlur}
        error={!!rawErrors?.length || !!validationError}
        variant="outlined"
      />
      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText>{rawErrors.join(', ')}</FormHelperText>
      )}
    </FormControl>
  );
};
